// ============================================================================
// COMPONENTE: OnboardingTour.jsx — C4
// Tour guiado de 4 telas exibido apenas na primeira vez que o cliente acessa.
// Flag localStorage: rda_onboarding_done_{clientId}
// ============================================================================

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Briefcase, Home, MessageSquare, Wallet, X, ChevronRight } from 'lucide-react';
import { usePortalContext } from '../../context/PortalContext';

const STEPS = [
  {
    icon: <Home className="w-8 h-8 text-[#C5A059]" />,
    title: 'Bem-vindo ao Portal RDA',
    body: 'Aqui você acompanha tudo sobre o seu caso, documentos e financeiro em um só lugar, direto do seu celular ou computador.',
  },
  {
    icon: <Briefcase className="w-8 h-8 text-[#C5A059]" />,
    title: 'Meu Caso',
    body: 'Acesse os cadernos do processo, veja documentos compartilhados e acompanhe o histórico de atualizações.',
  },
  {
    icon: <Wallet className="w-8 h-8 text-[#C5A059]" />,
    title: 'Financeiro',
    body: 'Visualize suas faturas, valores e status de pagamento. Você poderá pagar direto pelo portal em breve.',
  },
  {
    icon: <MessageSquare className="w-8 h-8 text-[#C5A059]" />,
    title: 'Mensagens',
    body: 'Comunique-se diretamente com o escritório de forma segura e privada, sem precisar do WhatsApp.',
  },
];

export default function OnboardingTour() {
  const { crmClient } = usePortalContext();
  const [step, setStep]       = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const clientId = crmClient?.id;
    if (!clientId) return;
    try {
      const done = localStorage.getItem(`rda_onboarding_done_${clientId}`);
      if (!done) setVisible(true);
    } catch {}
  }, [crmClient?.id]);

  const handleClose = () => {
    try {
      const clientId = crmClient?.id;
      if (clientId) localStorage.setItem(`rda_onboarding_done_${clientId}`, '1');
    } catch {}
    setVisible(false);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else handleClose();
  };

  if (!visible) return null;

  const current = STEPS[step];

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}>
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#20243B] px-4 py-3 flex items-center justify-between">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-[#C5A059]' : i < step ? 'w-3 bg-[#C5A059]/50' : 'w-3 bg-white/20'}`}
              />
            ))}
          </div>
          <button type="button" onClick={handleClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-7 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-2xl bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center">
              {current.icon}
            </div>
          </div>
          <h2 className="text-lg font-extrabold text-[#20243B] leading-tight">{current.title}</h2>
          <p className="mt-2 text-sm text-gray-500 leading-relaxed">{current.body}</p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-400">{step + 1} de {STEPS.length}</span>
          <button
            type="button"
            onClick={handleNext}
            className="flex items-center gap-1.5 px-5 py-2.5 bg-[#C5A059] text-[#20243B] font-extrabold text-sm rounded-xl"
          >
            {step < STEPS.length - 1 ? 'Próximo' : 'Entendido'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
